import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import FavoriteButton from '@/components/base/FavoriteButton';
import StatusBadge from '@/components/base/StatusBadge';
import SectionHeading from '@/components/base/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useTrackRecentView } from '@/hooks/useRecentViews';
import { TYPE_META, REGION_OPTIONS, formatDate, getRelatedWorks, getWorkById } from '@/lib/works';
import { TYPE_LABELS, REGION_LABELS, STATUS_LABELS } from '@/types/work';

const CONTAINER = 'mx-auto w-full max-w-[1240px] px-4 md:px-8 lg:px-10';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = id ? getWorkById(id) : undefined;
  const [toast, setToast] = useState('');

  useTrackRecentView(work?.id);

  usePageMeta(
    work ? `${work.title} - 日韩中文字幕详细介绍` : '作品未找到 - 日韩中文字幕',
    work
      ? `${work.title}（${work.originalTitle}）${TYPE_LABELS[work.type]}，${REGION_LABELS[work.region]}${work.year}年作品，中文字幕状态：${STATUS_LABELS[work.subtitleStatus]}。`
      : '未找到该影视作品，请返回首页重新浏览。'
  );

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        setToast('页面链接已复制，快分享给同样爱看日韩影视的朋友吧');
      } else {
        setToast('请手动复制浏览器地址栏链接进行分享');
      }
    } catch {
      setToast('复制失败，请手动复制浏览器地址栏链接');
    }
  };

  if (!work) {
    return (
      <div className={`${CONTAINER} flex flex-col items-center py-24 text-center`}>
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-search-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">作品可能已更名或链接有误，请返回首页重新浏览。</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const meta = TYPE_META[work.type];
  const related = getRelatedWorks(work, 4);
  const regionLabel = REGION_OPTIONS.find((item) => item.value === work.region)?.label;

  return (
    <>
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={work.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-cover blur-2xl opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/70"></div>
        </div>

        <div className={`${CONTAINER} relative py-8 md:py-12`}>
          <div className="[&_a]:text-background-200 [&_span]:text-background-300">
            <Breadcrumb
              items={[
                { label: '主页', to: '/' },
                { label: meta.label, to: `/category/${work.type}` },
                { label: work.title },
              ]}
            />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-10">
            <div className="mx-auto w-full max-w-[260px] lg:mx-0 lg:max-w-none">
              <div className="overflow-hidden rounded-lg border border-background-50/15">
                <img
                  src={work.cover}
                  alt={`${work.title} 中文字幕封面`}
                  title={`${work.title} - 日韩中文字幕`}
                  className="aspect-[2/3] w-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex gap-3">
                <FavoriteButton id={work.id} variant="solid" />
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>

            <div className="text-background-50">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={work.subtitleStatus} />
                <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-100">
                  {meta.label}
                </span>
                <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-100">
                  {regionLabel}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-4xl">
                {work.title}
              </h1>
              <p className="mt-2 text-sm text-background-200">{work.originalTitle}</p>

              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-background-100 md:text-base">
                {work.description}
              </p>

              <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[
                  { label: '类型', value: meta.label, icon: meta.icon },
                  { label: '地区', value: regionLabel || '-', icon: 'ri-map-pin-line' },
                  { label: '上映年份', value: String(work.year), icon: 'ri-calendar-line' },
                  { label: '字幕更新', value: formatDate(work.updatedAt), icon: 'ri-refresh-line' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-50/15 bg-background-50/5 p-3.5">
                    <dt className="flex items-center gap-1.5 text-xs text-background-300">
                      <i className={item.icon}></i>
                      {item.label}
                    </dt>
                    <dd className="mt-1.5 font-heading text-sm font-semibold text-background-50">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-xs text-background-300">标签：</span>
                {work.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-background-50/12 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-accent-500 hover:text-background-50"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`${CONTAINER} py-12 md:py-16`}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px] lg:gap-12">
          <div className="space-y-10">
            <div>
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
                剧情简介
              </h2>
              <div className="mt-5 space-y-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="text-sm leading-loose text-foreground-700 md:text-base">{work.synopsis}</p>
                <div className="rounded-md border border-primary-200 bg-primary-50 p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold text-primary-700">
                    <i className="ri-information-line"></i>
                    中文字幕情况说明
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{work.subtitleNote}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
                字幕文件信息
              </h2>
              <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
                <div className="hidden grid-cols-[1.1fr_1.3fr_0.8fr_1fr_auto] gap-4 bg-background-100 px-5 py-3 text-xs font-semibold text-foreground-600 md:grid">
                  <span>语言</span>
                  <span>制作组</span>
                  <span>格式</span>
                  <span>发布时间</span>
                  <span className="text-right">操作</span>
                </div>
                <ul className="divide-y divide-background-200 bg-background-50">
                  {work.subtitleVersions.map((version) => (
                    <li
                      key={version.id}
                      className="grid grid-cols-1 gap-2 px-5 py-4 md:grid-cols-[1.1fr_1.3fr_0.8fr_1fr_auto] md:items-center md:gap-4"
                    >
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                        <i className="ri-translate-2 text-primary-500"></i>
                        {version.language}
                      </div>
                      <div className="text-sm text-foreground-600">{version.team}</div>
                      <div>
                        <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                          {version.format}
                        </span>
                      </div>
                      <div className="text-sm text-foreground-600">{formatDate(version.publishedAt)}</div>
                      <div className="md:text-right">
                        <button
                          type="button"
                          onClick={() => setToast('此处为字幕信息展示，暂不提供文件下载')}
                          className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                        >
                          <i className="ri-eye-line"></i>
                          查看详情
                        </button>
                      </div>
                      {version.note && (
                        <p className="text-xs text-accent-600 md:col-span-5 md:-mt-1">备注：{version.note}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                <i className="ri-error-warning-line mt-0.5 text-accent-500"></i>
                本站仅展示字幕信息，不提供影视文件与字幕文件下载。以上版本信息供查找与交流参考。
              </p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">作品信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  { label: '中文名', value: work.title },
                  { label: '原名', value: work.originalTitle },
                  { label: '题材', value: work.genre },
                  { label: '语言', value: `${regionLabel}语` },
                  { label: '字幕状态', value: STATUS_LABELS[work.subtitleStatus] },
                  { label: '完整版本', value: `${work.subtitleVersions.length} 个` },
                ].map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="text-right font-medium text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to={`/category/${work.type}`}
                className="mt-5 flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                查看全部{meta.label}字幕
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-accent-800">
                <i className="ri-bookmark-3-line"></i>
                收藏状态
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                收藏仅保存在你的本地浏览器中，刷新页面后依然保留，不会上传到服务器。
              </p>
              <div className="mt-4">
                <FavoriteButton id={work.id} variant="solid" />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100 py-12 md:py-16">
          <div className={CONTAINER}>
            <SectionHeading
              title="相关推荐"
              subtitle={`与《${work.title}》同类型或标签相近的影视作品`}
              moreTo={`/category/${work.type}`}
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div className="flex items-center gap-2.5 rounded-lg border border-accent-400/40 bg-foreground-950 px-5 py-3 text-sm text-background-50">
            <i className="ri-information-line text-accent-400"></i>
            {toast}
          </div>
        </div>
      )}
    </>
  );
}